"use strict";
// Copyright 2020 Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
Object.defineProperty(exports, "__esModule", { value: true });
var SimulcastTransceiverController_1 = require("../transceivercontroller/SimulcastTransceiverController");
var DefaultVideoCaptureAndEncodeParameter_1 = require("../videocaptureandencodeparameter/DefaultVideoCaptureAndEncodeParameter");
var BitrateParameters_1 = require("./BitrateParameters");
/**
 * [[SimulcastUplinkPolicy]] determines capture and encode
 *  parameters that reacts to estimated uplink bandwidth
 */
var SimulcastUplinkPolicy = /** @class */ (function () {
    function SimulcastUplinkPolicy(selfAttendeeId, logger) {
        this.selfAttendeeId = selfAttendeeId;
        this.logger = logger;
        this.numParticipants = 0;
        this.newQualityMap = new Map();
        this.currentQualityMap = new Map();
        this.lastUplinkBandwidthKbps = 2800;
        this.newMediaTrackConstraints = {};
        this.currMediaTrackConstraints = {};
        this.lastUpdatedMs = Date.now();
        this.uplinkMeasurementWindow = [];
        this.videoIndex = null;
        this.currLocalDescriptions = [];
        this.nextLocalDescriptions = [];
        this.optimalParameters = new DefaultVideoCaptureAndEncodeParameter_1.default(0, 0, 0, 0, true);
        this.parametersInEffect = new DefaultVideoCaptureAndEncodeParameter_1.default(0, 0, 0, 0, true);
        for (var i = 0; i < SimulcastUplinkPolicy.windowSize; i++) {
            this.uplinkMeasurementWindow.push(SimulcastUplinkPolicy.defaultUplinkBandwidthKbps);
        }
        this.lastUplinkBandwidthKbps = this.getWindowsUplinkKbps();
        this.currentQualityMap = this.fillEncodingParamWithBitrates([300, 500, 1100]);
        this.newQualityMap = this.fillEncodingParamWithBitrates([300, 500, 1100]);
        this.newMediaTrackConstraints = this.fillMediaTrackConstraints();
        this.currMediaTrackConstraints = this.fillMediaTrackConstraints();
    }
    SimulcastUplinkPolicy.prototype.getWindowsUplinkKbps = function () {
        var weightedAverage = this.uplinkMeasurementWindow[0] * 0.05 +
            this.uplinkMeasurementWindow[1] * 0.15 +
            this.uplinkMeasurementWindow[2] * 0.2 +
            this.uplinkMeasurementWindow[3] * 0.25 +
            this.uplinkMeasurementWindow[4] * 0.35;
        return weightedAverage;
    };
    SimulcastUplinkPolicy.prototype.updateWindowsUplinkKbps = function (uplinkKbps) {
        this.uplinkMeasurementWindow.shift();
        this.uplinkMeasurementWindow.push(uplinkKbps);
        return this.getWindowsUplinkKbps();
    };
    SimulcastUplinkPolicy.prototype.updateConnectionMetric = function (_b) {
        var _this = this;
        var _c = (_b === void 0 ? {} : _b).uplinkKbps, uplinkKbps = _c === void 0 ? 0 : _c;
        if (isNaN(uplinkKbps)) {
            return;
        }
        this.lastUplinkBandwidthKbps = this.updateWindowsUplinkKbps(uplinkKbps);
        this.logger.debug(function () {
            return "simulcast: uplink policy update metrics " + uplinkKbps + "Kbps\n      windowed " + _this.lastUplinkBandwidthKbps + "Kbps";
        });
        if (Date.now() < this.lastUpdatedMs + SimulcastUplinkPolicy.holdDownDurationMs) {
            return;
        }
        this.newQualityMap = this.calculateEncodingParameters();
        this.newMediaTrackConstraints = this.fillMediaTrackConstraints();
    };
    SimulcastUplinkPolicy.prototype.calculateEncodingParameters = function () {
        // bitrates parameter min is not used for now
        var newBitrates = [
            new BitrateParameters_1.default(),
            new BitrateParameters_1.default(),
            new BitrateParameters_1.default(),
        ];
        if (this.numParticipants <= 2 && this.lastUplinkBandwidthKbps >= 1600) {
            // 320x180+ (640x360) +  + 1280x720
            newBitrates[0].maxBitrateKbps = 300;
            newBitrates[1].maxBitrateKbps = 0;
            newBitrates[2].maxBitrateKbps = 1200;
        }
        else if (this.numParticipants <= 2 && this.lastUplinkBandwidthKbps >= 1100) {
            // 240 * 140 + (480x280) + 960x560
            newBitrates[0].maxBitrateKbps = 200;
            newBitrates[1].maxBitrateKbps = 0;
            newBitrates[2].maxBitrateKbps = 900;
        }
        else if (this.numParticipants <= 4 && this.lastUplinkBandwidthKbps >= 600) {
            // 240 * 140 + (480x280) + 960x560
            newBitrates[0].maxBitrateKbps = 200;
            newBitrates[1].maxBitrateKbps = 0;
            newBitrates[2].maxBitrateKbps = 600;
        }
        else {
            // 640x360
            newBitrates[0].maxBitrateKbps = 200;
            newBitrates[1].maxBitrateKbps = 400;
            newBitrates[2].maxBitrateKbps = 0;
        }
        var bitrates = newBitrates.map(function (v, _i, _a) {
            return v.maxBitrateKbps;
        });
        this.newQualityMap = this.fillEncodingParamWithBitrates(bitrates);
        return this.newQualityMap;
    };
    SimulcastUplinkPolicy.prototype.chooseMediaTrackConstraints = function () {
        this.currMediaTrackConstraints = this.newMediaTrackConstraints;
        return this.currMediaTrackConstraints;
    };
    SimulcastUplinkPolicy.prototype.chooseEncodingParameters = function () {
        this.currentQualityMap = this.newQualityMap;
        return this.currentQualityMap;
    };
    SimulcastUplinkPolicy.prototype.updateIndex = function (videoIndex) {
        // the +1 for self is assuming that we intend to send video, since
        // the context here is VideoUplinkBandwidthPolicy
        this.numParticipants =
            videoIndex.numberOfVideoPublishingParticipantsExcludingSelf(this.selfAttendeeId) + 1;
        this.optimalParameters = new DefaultVideoCaptureAndEncodeParameter_1.default(this.captureWidth(), this.captureHeight(), this.captureFrameRate(), this.maxBandwidthKbps(), false);
        this.videoIndex = videoIndex;
        this.newQualityMap = this.calculateEncodingParameters();
        this.newMediaTrackConstraints = this.fillMediaTrackConstraints();
    };
    SimulcastUplinkPolicy.prototype.wantsResubscribe = function () {
        var e_1, _b;
        var constraintDiff = !(JSON.stringify(this.currMediaTrackConstraints.width) ===
            JSON.stringify(this.newMediaTrackConstraints.width) &&
            JSON.stringify(this.currMediaTrackConstraints.height) ===
                JSON.stringify(this.newMediaTrackConstraints.height) &&
            JSON.stringify(this.currMediaTrackConstraints.frameRate) ===
                JSON.stringify(this.newMediaTrackConstraints.frameRate));
        try {
            for (var _c = __values(SimulcastTransceiverController_1.default.NAME_ARR_ASCENDING), _d = _c.next(); !_d.done; _d = _c.next()) {
                var ridName = _d.value;
                constraintDiff =
                    constraintDiff ||
                        !this.compareEncodingParameter(this.newQualityMap.get(ridName), this.currentQualityMap.get(ridName));
                if (constraintDiff) {
                    break;
                }
            }
        }
        catch (e_1_1) { e_1 = { error: e_1_1 }; }
        finally {
            try {
                if (_d && !_d.done && (_b = _c.return)) _b.call(_c);
            }
            finally { if (e_1) throw e_1.error; }
        }
        this.nextLocalDescriptions = this.videoIndex.localStreamDescriptions();
        var _loop_1 = function (i) {
            var streamId = this_1.nextLocalDescriptions[i].streamId;
            if (streamId !== 0 && !!streamId) {
                var prevIndex = this_1.currLocalDescriptions.findIndex(function (val) {
                    return val.streamId === streamId;
                });
                if (prevIndex !== -1) {
                    if (this_1.nextLocalDescriptions[i].disabledByWebRTC !==
                        this_1.currLocalDescriptions[prevIndex].disabledByWebRTC) {
                        constraintDiff = true;
                    }
                }
            }
        };
        var this_1 = this;
        for (var i = 0; i < this.nextLocalDescriptions.length; i++) {
            _loop_1(i);
        }
        if (constraintDiff) {
            this.lastUpdatedMs = Date.now();
        }
        this.currLocalDescriptions = this.nextLocalDescriptions;
        return constraintDiff;
    };
    SimulcastUplinkPolicy.prototype.compareEncodingParameter = function (encoding1, encoding2) {
        return JSON.stringify(encoding1) === JSON.stringify(encoding2);
    };
    SimulcastUplinkPolicy.prototype.chooseCaptureAndEncodeParameters = function () {
        // should deprecate in this policy
        this.parametersInEffect = this.optimalParameters.clone();
        return this.parametersInEffect.clone();
    };
    SimulcastUplinkPolicy.prototype.captureWidth = function () {
        // should deprecate in this policy
        var width = 1280;
        return width;
    };
    SimulcastUplinkPolicy.prototype.captureHeight = function () {
        // should deprecate in this policy
        var height = 720;
        return height;
    };
    SimulcastUplinkPolicy.prototype.captureFrameRate = function () {
        // should deprecate in this policy
        return 15;
    };
    SimulcastUplinkPolicy.prototype.maxBandwidthKbps = function () {
        // should deprecate in this policy
        return 1400;
    };
    SimulcastUplinkPolicy.prototype.setIdealMaxBandwidthKbps = function (_idealMaxBandwidthKbps) {
        // should deprecate in this policy
    };
    SimulcastUplinkPolicy.prototype.setHasBandwidthPriority = function (_hasBandwidthPriority) {
        // should deprecate in this policy
    };
    SimulcastUplinkPolicy.prototype.fillEncodingParamWithBitrates = function (bitratesKbps) {
        var newMap = new Map();
        var toBps = 1000;
        var nameArr = SimulcastTransceiverController_1.default.NAME_ARR_ASCENDING;
        var bitrateArr = bitratesKbps;
        var scale = 4;
        for (var i = 0; i < nameArr.length; i++) {
            var ridName = nameArr[i];
            newMap.set(ridName, {
                rid: ridName,
                active: bitrateArr[i] > 0 ? true : false,
                scaleResolutionDownBy: scale,
                maxBitrate: bitrateArr[i] * toBps,
            });
            scale = scale / 2;
        }
        return newMap;
    };
    SimulcastUplinkPolicy.prototype.fillMediaTrackConstraints = function () {
        var trackConstraint;
        if (this.numParticipants <= 2 && this.lastUplinkBandwidthKbps >= 1100) {
            trackConstraint = {
                width: { ideal: 1280 },
                height: { ideal: 720 },
                frameRate: { ideal: 15 },
            };
        }
        else if (this.numParticipants <= 4 && this.lastUplinkBandwidthKbps >= 600) {
            trackConstraint = {
                width: { ideal: 960 },
                height: { ideal: 540 },
                frameRate: { ideal: 15 },
            };
        }
        else {
            trackConstraint = {
                width: { ideal: 1280 },
                height: { ideal: 720 },
                frameRate: { ideal: 15 },
            };
        }
        return trackConstraint;
    };
    SimulcastUplinkPolicy.defaultUplinkBandwidthKbps = 2800;
    SimulcastUplinkPolicy.windowSize = 5;
    SimulcastUplinkPolicy.holdDownDurationMs = 4000;
    SimulcastUplinkPolicy.defaultMaxFrameRate = 15;
    return SimulcastUplinkPolicy;
}());
exports.default = SimulcastUplinkPolicy;
//# sourceMappingURL=SimulcastUplinkPolicy.js.map